import React from "react";
import Text from "./Text";
import SidebarFloorSelectorButton from "./SidebarFloorSelectorButton";
import { MetadataItem } from "../types/metadata";
import { useTranslation } from "react-i18next";

type Building = {
  readonly id: string;
  readonly label: string;
  readonly floors: readonly number[]; //
};

type SidebarFloorSelectorProps = {
  BUILDINGS: readonly Building[];
  filteredItems: MetadataItem[];
  selectedBuilding: string | null;
  selectedFloor: number | null;
  onFloorSelect: (buildingId: string, floor: number) => void;
  cardRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  listContainerRef: React.RefObject<HTMLDivElement | null>;
  isMobile?: boolean;
};

const SidebarFloorSelector: React.FC<SidebarFloorSelectorProps> = ({
  BUILDINGS,
  filteredItems,
  selectedBuilding,
  selectedFloor,
  onFloorSelect,
  cardRefs,
  listContainerRef,
  isMobile = false,
}) => {
  // 翻訳インスタンスのインポート
  const { t } = useTranslation();
  /**
   * 階数ボタンクリック時の処理
   */
  const handleFloorClick = (buildingId: string, floor: number) => {
    onFloorSelect(buildingId, floor);

    const card = cardRefs.current?.[buildingId + "-" + floor];
    if (card && listContainerRef.current) {
      card.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  if (isMobile) {
    return (
      <div className="flex flex-row gap-6">
        {BUILDINGS.map((b) => (
          <div key={b.id} className="flex flex-col gap-1">
            <Text as="span" variant="body/s" weight="bold" className="text-TextPrimary">
              {t(b.label)}
            </Text>
            <div className="flex flex-row gap-1.5">
              {b.floors.map((f) => {
                const hasRoom = filteredItems.some(
                  (item) => item.building === b.id && item.floor === f,
                );
                const isSelected = selectedBuilding === b.id && selectedFloor === f;

                return (
                  <SidebarFloorSelectorButton
                    key={f}
                    isSelected={isSelected}
                    isDisabled={!hasRoom}
                    onClick={hasRoom ? () => handleFloorClick(b.id, f) : undefined}
                  >
                    {f}
                  </SidebarFloorSelectorButton>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 items-center w-[72px] min-w-[72px]">
      {BUILDINGS.map((b) => (
        <div key={b.id} className="flex flex-col items-center gap-2 mb-2">
          <Text
            as="div"
            variant="body/m"
            weight="bold"
            className="text-TextPrimary text-center leading-tight break-words w-full"
          >
            {t(b.label)}
          </Text>

          <div className="flex flex-col gap-2">
            {b.floors.map((f) => {
              const hasRoom = filteredItems.some(
                (item) => item.building === b.id && item.floor === f,
              );
              const isSelected = selectedBuilding === b.id && selectedFloor === f;

              return (
                <SidebarFloorSelectorButton
                  key={f}
                  isSelected={isSelected}
                  isDisabled={!hasRoom}
                  onClick={hasRoom ? () => handleFloorClick(b.id, f) : undefined}
                >
                  {f}
                </SidebarFloorSelectorButton>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SidebarFloorSelector;
